import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Badge } from "../../components/ui-extras";

function _fmtIso(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
}

function StatusBadge({ status }) {
  if (status === "approved") return <Badge variant="success">approved</Badge>;
  if (status === "rejected") return <Badge variant="error">rejected</Badge>;
  return <Badge variant="info">pending</Badge>;
}

export default function CustomerChangeRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    api.get("/portal/card-change-requests", { params })
      .then(({ data }) => setRows(data || []))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  return (
    <>
      <PageHeader
        title="Card change requests"
        subtitle="PORTAL / CHANGE REQUESTS"
        testId="portal-ccr-header"
      />

      <div className="gf-card mb-4">
        <div className="gf-card-body flex items-center justify-between gap-3">
          <div className="text-sm text-zinc-600">
            History of every card-detail change you&apos;ve requested (Driver,
            Unit, Phone, PIN). Pending requests are typically reviewed within
            24 hours.
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="gf-input"
            data-testid="portal-ccr-status-filter"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div className="gf-card">
        <table className="gf-table" data-testid="portal-ccr-table">
          <thead>
            <tr>
              <th>SUBMITTED</th>
              <th>CARD</th>
              <th>FIELD</th>
              <th>FROM → TO</th>
              <th>STATUS</th>
              <th>REVIEWED</th>
              <th>NOTE</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="text-center text-zinc-400 py-10">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="text-center text-zinc-400 py-10"
                  data-testid="portal-ccr-empty"
                >
                  No change requests yet. Open the <strong>Cards</strong> page
                  to submit one.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.request_id}
                data-testid={`portal-ccr-row-${r.request_id}`}
              >
                <td className="font-mono text-xs">{_fmtIso(r.requested_at)}</td>
                <td className="font-mono text-xs">····{r.card_last5}</td>
                <td>
                  <Badge variant="info">
                    {{
                      driver_name: "Driver",
                      unit: "Unit #",
                      phone: "Phone #",
                      pin: "PIN #",
                    }[r.field_name] || r.field_name}
                  </Badge>
                </td>
                <td className="text-xs">
                  <span className="text-zinc-500">{r.old_value || "—"}</span>
                  <span className="mx-1 text-zinc-300">→</span>
                  <span className="text-zinc-900 font-medium">{r.new_value}</span>
                </td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td className="font-mono text-xs">
                  {r.reviewed_at ? _fmtIso(r.reviewed_at) : "—"}
                </td>
                <td
                  className="text-xs max-w-[200px] break-words text-zinc-600"
                  data-testid={`portal-ccr-note-${r.request_id}`}
                >
                  {r.admin_note || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
